// File: src/pages/mindspark/MindSparkFAQ.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";

const faqData = {
  "Student / Learner": [
    // Negative
    {
      sentiment: "negative",
      question: "I'm overwhelmed with studies — how can MindSpark fit in?",
      answer:
        "MindSpark is designed for quick daily interactions: 3-min quizzes, 5-min reflections via Chronicle, and Pomodoro Focus Timer sessions to boost productivity without adding stress.",
    },
    {
      sentiment: "negative",
      question: "What if I'm not good at self-reflection or journaling?",
      answer:
        "No worries! Start small with guided prompts in Chronicle and RCA tools that make 'Why?' analysis fun and structured, turning confusion into actionable insights.",
    },
    {
      sentiment: "negative",
      question: "Will using MindSpark take too much time from my routine?",
      answer:
        "It's flexible: Log moods in seconds, celebrate wins via Applause instantly, and access Youth Knowledge updates on-the-go via the mobile app.",
    },
    {
      sentiment: "negative",
      question: "I'm shy about sharing wins or joining communities — is that okay?",
      answer:
        "Absolutely private by default! Share Applause anonymously or with select friends, and engage at your comfort level in peer forums.",
    },
    {
      sentiment: "negative",
      question: "Can I really earn rewards or money as a student?",
      answer:
        "Yes! Earn points for quizzes and challenges, redeem for prizes via Leaderboard, or start small with Refer/Resell programs and Rewarding Ads.",
    },
    // Neutral
    {
      sentiment: "neutral",
      question: "What core tools does MindSpark offer for daily growth?",
      answer:
        "Chronicle for reflections, Applause for celebrations, RCA for problem-solving, and auto-generated Diary/Yearbook to cherish your journey.",
    },
    {
      sentiment: "neutral",
      question: "Is there a mobile app, and does it work offline?",
      answer:
        "Yes, the lightweight app supports offline logging for Chronicle and syncs when online; optimized for low-data usage.",
    },
    {
      sentiment: "neutral",
      question: "How do I access learning content like quizzes and news?",
      answer:
        "Daily Brain Fuel Quizzes (GK, current affairs, creativity) and top 10 Youth Knowledge updates are pushed to your dashboard.",
    },
    // Positive
    {
      sentiment: "positive",
      question: "How can MindSpark help with career and stress management?",
      answer:
        "AI Buddy Counselling for 24/7 support, Expert Mentoring for guidance, and Workshops/Podcasts on relationships, creativity, and self-growth.",
    },
    {
      sentiment: "positive",
      question: "What fun elements keep me motivated?",
      answer:
        "Gamified contests like Marksheet Challenge, Kharcho Jeeto, Aalu Dalo Sona Nikalo, plus MindSpark Games and badges for every milestone.",
    },
    {
      sentiment: "positive",
      question: "Can I create and share my own content?",
      answer:
        "Yes! Upcoming Creator Hub for writings/videos/art, POV for perspectives, Immersive Slides for visuals, and My Digital Card to showcase your identity.",
    },
  ],
  "Parent / Guardian": [
    {
      sentiment: "negative",
      question: "How do I know if MindSpark is safe for my child?",
      answer:
        "MindSpark prioritizes privacy with parent controls, no public sharing by default, and verified content/partners; aligned with child safety standards.",
    },
    {
      sentiment: "negative",
      question: "Will this distract my child from studies?",
      answer:
        "On the contrary: Tools like Focus Timer and Report Card Challenge enhance focus and track academic progress alongside personal growth.",
    },
    {
      sentiment: "neutral",
      question: "Can I monitor my child's progress?",
      answer:
        "Yes, shared dashboards show moods, habits, quiz scores, and Yearbook compilations, with options for family Applause and joint challenges.",
    },
    {
      sentiment: "neutral",
      question: "What family-oriented features are there?",
      answer:
        "Family access to Workshops, Leaderboards, and Nation Builder activities to foster collective growth and values alignment.",
    },
    {
      sentiment: "positive",
      question: "How does it build my child's confidence?",
      answer:
        "Through peer appreciation in Applause, badges from Games/Quizzes, and My Digital Card for proud sharing of achievements.",
    },
  ],
  "Teacher / Mentor": [
    {
      sentiment: "negative",
      question: "I'm already overloaded — why add MindSpark?",
      answer:
        "It streamlines mentoring: Host quick Workshops/Podcasts, track student RCA sessions, and use Quizzes for class engagement with minimal prep.",
    },
    {
      sentiment: "negative",
      question: "How do I get compensated for mentoring?",
      answer:
        "Pay-per-minute Expert Mentoring, plus opportunities to sponsor sessions or collaborate on content via Creator Hub.",
    },
    {
      sentiment: "neutral",
      question: "Can I integrate MindSpark into my curriculum?",
      answer:
        "Yes, embed RCA, Focus Timer, and Brain Fuel Quizzes into lessons; generate reports from Dashboard for student evaluations.",
    },
    {
      sentiment: "neutral",
      question: "What tools help me support student mental health?",
      answer:
        "AI Counselling referrals, Handwriting Analysis, and guided reflection prompts in Chronicle for early insights.",
    },
    {
      sentiment: "positive",
      question: "How can I recognize top students?",
      answer:
        "Nominate via Applause, feature in MindSpark Winners/Ranking, and award digital Collectibles or prizes from Leaderboard.",
    },
  ],
  "Young Entrepreneur": [
    {
      sentiment: "negative",
      question: "I have no products yet — can I still start?",
      answer:
        "Start with Refer/Resell programs, Rewarding Ads/Videos, or Creator Hub to build skills and audience before selling.",
    },
    {
      sentiment: "neutral",
      question: "What commerce tools are available?",
      answer:
        "Full Commerce Zone: List Products/Services/Rentals/Wholesale, Unity/Nearby Deals, ThinkXpress for quick sales, OrderFlow for MLM.",
    },
    {
      sentiment: "neutral",
      question: "How do I earn passive income?",
      answer:
        "Via Rewarding Videos/Ads, Become Aggregator/Franchise, or Marketplace trades with MindSpark Wallet for seamless payouts.",
    },
    {
      sentiment: "positive",
      question: "Can I gamify my business growth?",
      answer:
        "Yes! Contests like Aalu Dalo Sona Nikalo, Kharcho Jeeto for customer engagement, and Ranking for seller visibility.",
    },
  ],
  "Community Leader": [
    {
      sentiment: "negative",
      question: "How does MindSpark scale for group activities?",
      answer:
        "Group features for shared Leaderboards, collective Workshops, and community Dashboards to track group impact easily.",
    },
    {
      sentiment: "neutral",
      question: "Can we host custom events?",
      answer:
        "Yes, organize Immersive Magazine launches, Game tournaments, or Applause events via the platform's event tools.",
    },
    {
      sentiment: "positive",
      question: "How does it contribute to larger goals like Nation Builder?",
      answer:
        "Every activity feeds into personal/national growth metrics, with opportunities for youth-led initiatives and partnerships.",
    },
  ],
};

// Sentiment color mapping
const sentimentColors = {
  negative: "text-blue-400",
  neutral: "text-gray-400",
  positive: "text-green-400",
};

export default function MindSparkFAQ() {
  const stakeholders = Object.keys(faqData);
  const [selectedStakeholder, setSelectedStakeholder] = useState(stakeholders[0]);
  const [expandedIndex, setExpandedIndex] = useState(null);

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 sm:p-8">
      <h1 className="text-2xl sm:text-3xl font-bold mb-8 text-center text-blue-400">
        MindSpark – FAQ
      </h1>
      {/* Stakeholder Tabs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mb-8 justify-items-center">
        {stakeholders.map((stakeholder) => (
          <button
            key={stakeholder}
            onClick={() => {
              setSelectedStakeholder(stakeholder);
              setExpandedIndex(null);
            }}
            className={`px-4 py-2 rounded-lg font-semibold text-sm sm:text-base transition-all duration-200 ${
              selectedStakeholder === stakeholder
                ? "bg-blue-500 text-white shadow-lg"
                : "bg-gray-800 hover:bg-gray-700 text-gray-200"
            }`}
          >
            {stakeholder}
          </button>
        ))}
      </div>
      {/* FAQ List */}
      <div className="max-w-4xl mx-auto space-y-4">
        {faqData[selectedStakeholder].map((faq, index) => (
          <div
            key={index}
            className="bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden cursor-pointer transition-all hover:bg-gray-800/70"
            onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
          >
            <div className="flex justify-between items-center p-4">
              <h3 className={`font-medium text-lg ${sentimentColors[faq.sentiment]}`}>
                {faq.question}
              </h3>
              <span className={`text-2xl transition-transform ${expandedIndex === index ? 'rotate-180' : ''}`}>
                ▼
              </span>
            </div>
            {expandedIndex === index && (
              <div className="px-4 pb-4 text-gray-300">
                <p>{faq.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      {faqData[selectedStakeholder].length === 0 && (
        <p className="text-center text-gray-400 mt-8">No FAQs available for this category yet.</p>
      )}
      <footer className="bg-[#2a0f2f] border-t border-blue-800 text-blue-300 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs lg:text-sm">
            © 2025 Think5Whys Global DeepTech Solutions. All rights reserved.
          </div>
          <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
            <Link to="/about-us" className="text-white hover:text-blue-400">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-blue-400">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-blue-400">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-blue-400">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}